import { useEffect, useState } from "react";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import UsePaginationHook from "../../hooks/UsePaginationHook";
import { IOption, IPagination } from "../../data/AppInterface";
import {
  branchIcon,
  deleteIcon,
  eyeIcon,
  filterIcon,
  receiptLongIcon,
} from "../../assets/icons/SvgIconList";
import {
  AdvanceSearch,
  ICButton,
  ICConfirmationModal,
  ICTable,
} from "../../core-component/ui";
import {
  activeInactiveFirm,
  deleteFirm,
  FIRM_ADVANCE_SEARCH_INITIAL_VALUE,
  getAllFirms,
  getFirmFields,
  getFirmTypeDdl,
  IFirm,
  IFirmAdvancedSearch,
} from "./FirmListController";
import { ORDER_BY } from "../../utils/AppConstants";
import { BIT_VALUE } from "../../utils/AppEnumerations";
import {
  prepareMessageFromParams,
  toggleStatus,
} from "../../utils/AppFunctions";
import { ICCheckbox, ICDropDown } from "../../core-component/form";
import FirmForm from "./FirmForm";
import { useNavigate } from "react-router-dom";
import { routeAdmin, routeMaster } from "../../routes/AuthRoutePath";

let timer: ReturnType<typeof setTimeout>;

const FirmListView = () => {
  const navigation = useNavigate();
  const { t } = UseTranslationHook();
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [enterpriseSearch, setEnterpriseSearch] = useState("");
  const [firmList, setFirmList] = useState<IFirm[]>([]);
  const [firmType, setFirmType] = useState<IOption<string>[]>([]);
  const [firmToView, setFirmToView] = useState<IFirm>();
  const [advanceSearch, setAdvanceSearch] = useState<IFirmAdvancedSearch>(
    FIRM_ADVANCE_SEARCH_INITIAL_VALUE
  );
  const { pagination, setChangePagination } = UsePaginationHook();
  useEffect(() => {
    fetchFirm({ ...pagination, currentPage: 1 });
  }, [enterpriseSearch, advanceSearch]);

  useEffect(() => {
    fetchFirmType();
  }, []);

  const fetchFirm = (paginationPlayLoad: IPagination) => {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(async () => {
      setIsLoading(true);
      await getAllFirms(
        setFirmList,
        paginationPlayLoad,
        setChangePagination,
        enterpriseSearch,
        advanceSearch
      );
      setIsLoading(false);
    }, 500);
  };

  const fetchFirmType = async () => {
    await getFirmTypeDdl(setFirmType);
  };

  const handleFirmStatus = async (values: IFirm) => {
    const isStatusChange =
      values.is_deleted === BIT_VALUE.Zero &&
      (await activeInactiveFirm(
        values.id,
        values.is_active === BIT_VALUE.One ? BIT_VALUE.Zero : BIT_VALUE.One
      ));
    if (isStatusChange) {
      setFirmList(toggleStatus(firmList, values.id));
    }
  };

  const customActionView = (item: IFirm) => {
    return (
      <div className="flex item-center justify-center w-full">
        <div
          className="size-5 cursor-pointer"
          title={t("view_tooltip_icon")}
          onClick={() => {
            setFirmToView(item);
            setShowForm(true);
          }}
        >
          {eyeIcon}
        </div>

        {item.is_deleted === BIT_VALUE.Zero && (
          <div
            className="ml-2 size-5 cursor-pointer"
            title={t("delete_tooltip_icon")}
            onClick={() => {
              setShowDeleteConfirmation(true);
              setFirmToView(item);
            }}
          >
            {deleteIcon}
          </div>
        )}

        <div
          className="ml-2 size-5 cursor-pointer"
          title={t("view_branch_tooltip_icon")}
          onClick={() => {
            navigation("/" + routeMaster.admin + "/" + routeAdmin.branch, {
              state: { id: item.id, is_delete: item.is_deleted },
            });
          }}
        >
          {branchIcon}
        </div>
        <div
          className="ml-2 size-5 cursor-pointer"
          title={t("view_payment_transaction_tooltip_icon")}
          onClick={() => {
            navigation(
              "/" + routeMaster.admin + "/" + routeAdmin.paymentTransaction,
              {
                state: { id: item.id },
              }
            );
          }}
        >
          {receiptLongIcon}
        </div>
      </div>
    );
  };

  const handleDelete = async (value: boolean) => {
    if (value && firmToView) {
      const data = await deleteFirm(firmToView.id);
      if (data) {
        fetchFirm(pagination);
      }
    }
    setShowDeleteConfirmation(false);
  };

  const filterView = (
    <div className="w-full flex">
      <ICDropDown
        className="min-w-72"
        placeholder={t("firm_type")}
        selected={advanceSearch.firm_type}
        options={firmType}
        onSelect={(option) =>
          setAdvanceSearch((prev) => ({
            ...prev,
            firm_type: option.data_value,
          }))
        }
      />

      <ICCheckbox
        label={t("show_deleted")}
        checkboxClassName="ml-4"
        checked={advanceSearch.is_deleted === BIT_VALUE.One}
        onChange={(option) =>
          setAdvanceSearch((prev) => ({
            ...prev,
            is_deleted: option ? BIT_VALUE.One : BIT_VALUE.Zero,
          }))
        }
      />
      <div className="!py-0 ml-4">
        <ICButton
          onClick={() => setAdvanceSearch(FIRM_ADVANCE_SEARCH_INITIAL_VALUE)}
          className="flex !w-fit"
        >
          <div className="size-5 grid place-self-center">{filterIcon}</div>
          &nbsp;{t("clear")}
        </ICButton>
      </div>
    </div>
  );

  return (
    <div className="w-full overflow-auto scrollbar-remove p-4">
      <AdvanceSearch
        handleSearchChange={(text) => {
          setEnterpriseSearch(text);
        }}
        filterNode={filterView}
        searchText={enterpriseSearch}
        refreshTable={() => {
          fetchFirm(pagination);
        }}
      />

      <ICTable<IFirm>
        isLoading={isLoading}
        totalFlex="grid grid-cols-15"
        fields={getFirmFields(customActionView, handleFirmStatus, t)}
        data={firmList}
        pagination={pagination}
        handleChangePerPageRows={(perPageRow) =>
          fetchFirm({
            ...pagination,
            perPageRows: perPageRow,
            currentPage: 1,
          })
        }
        handleChangePage={(page) =>
          fetchFirm({ ...pagination, currentPage: page })
        }
        handleSort={(field) =>
          fetchFirm({
            ...pagination,
            sortBy: field.key,
            orderBy:
              pagination.orderBy === ORDER_BY.desc
                ? ORDER_BY.asc
                : ORDER_BY.desc,
          })
        }
      />

      {showForm && firmToView && (
        <FirmForm
          handleClose={() => setShowForm(false)}
          firmToView={firmToView}
        />
      )}
      {showDeleteConfirmation && firmToView && (
        <ICConfirmationModal
          title={t("delete_firm")}
          message={prepareMessageFromParams(t("delete_message"), [
            ["fieldName", firmToView.firm_name],
          ])}
          onClose={(value) => handleDelete(value)}
        />
      )}
    </div>
  );
};

export default FirmListView;
